import { ComplexTable } from '@synergy/table';
import { Col, Row } from 'antd';
import clsx from 'clsx';
import i18next from "i18next";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import query from '../../../../../server/constants.api';
import useFetch from "../../../../../server/useFetch";
import { PageContent, PageSection, PageTitle } from "../../../../../styles/global/Pages.styled";
import { handleNotificationWithAction } from '../../../../../utils/config/notificationHandler';
import Spinner from '../../../Atomics/Spinner';
import { Text } from '../../../Atomics/Text';
import permissionToEdit from '../AccessUtils';
import { serializeThresholdData, thresholdEmptyRecord } from './ThresholdsConfig/ThresholdsConfig';
import ThresholdsModal from './ThresholdsModal/ThresholdsModal';

export const ThresholdsConfigurations = ({ ...props }) => {

    const allowedToWrite = permissionToEdit(['Configurations', 'Events', 'ThresholdsConfiguration'])
    const { t } = useTranslation();
    const tenant = JSON.parse(localStorage.getItem('unified_nova_tenant'));
    const [thresholdToDelete, setThresholdToDelete] = useState(null);

    // -------------- New States --------------
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalState, setModalState] = useState(null); // null, 'create', 'edit', 'view'
    const [listingData, setListingData] = useState(null);

    const {
        data: thresholds,
        refetch: refetchThresholdList,
        remove: removeThresholdList,
        isFetching: isFetchingThresholds,
    } = useFetch(
        "/configurations/thresholds",
        "getThresholds",
        { tenant: tenant[0] },
        null,
        null,
        query.middlewareURL
    );

    const {
        refetch: refetchRemoveThreshold,
        isFetching: isFetchingRemoveThreshold,
        isSuccess: isSuccessRemoveThreshold,
        isError: isErrorRemoveThreshold,
        error: errorRemoveThreshold,
        remove: removeRemoveThreshold
    } = useFetch("/configurations/thresholds",
        "deleteThreshold",
        { ...thresholdToDelete },
        false,
        "DELETE",
        query.middlewareURL
    );

    /**
     * Make the first fetch when the component is mounted of the thresholds
     * And remove the fetch when the component is unmounted
     */
    useEffect(() => {
        if (tenant[0]) {
            refetchThresholdList();
        }

        return () => {
            removeThresholdList();
        }
    }, []);

    /**
     * Serialize the thresholds data when the thresholds state is set
     */
    useEffect(() => {
        setListingData(serializeThresholdData(thresholds))
    }, [thresholds])

    /**
     * Make the fetch to remove the threshold when the thresholdToDelete state is set
     */
    useEffect(() => {
        if (thresholdToDelete) {
            refetchRemoveThreshold();
        }
    }, [thresholdToDelete])

    /**
     * Handle the notification for the remove threshold fetch
     * And remove the threshold from the listing data when the operation is successful
     */
    useEffect(() => {
        const successMessage = "notification.success_remove_threshold";
        const errorMessage = "notification.error_remove_threshold";

        handleNotificationWithAction(isFetchingRemoveThreshold, isSuccessRemoveThreshold, isErrorRemoveThreshold, errorRemoveThreshold,
            successMessage, errorMessage, t,
            onRemoveThresholdSuccess, onRemoveThresholdSuccessOrError);

    }, [isErrorRemoveThreshold, isSuccessRemoveThreshold, isFetchingRemoveThreshold])

    /**
     * Handles the successful removal of a threshold.
     */
    const onRemoveThresholdSuccess = () => {
        refetchThresholdList();
    }

    /**
     * Callback function called when removing a threshold is successful or encounters an error.
     */
    const onRemoveThresholdSuccessOrError = () => {
        setThresholdToDelete(null);
        removeRemoveThreshold();
    }

    const ThresholdsTableConfig = [
        {
            title: t('Item Name'),
            dataIndex: 'itemName'
        },
        {
            title: t('Label'),
            dataIndex: 'label'
        },
        {
            title: t('Type'),
            dataIndex: 'type'
        },
        {
            title: t('Open Threshold'),
            dataIndex: 'openThreshold'
        },
        {
            title: t('Close Threshold'),
            dataIndex: 'closeThreshold'
        },
        {
            title: t('Urgency'),
            dataIndex: 'urgency'
        },
        {
            title: t('Generate Incident'),
            dataIndex: 'generateIncident'
        },
        {
            title: t('Tenant'),
            dataIndex: 'tenant'
        }
    ]

    const openModal = (state) => setModalState(state);

    const closeModal = () => {
        setModalState(null);
        setSelectedItem(null);
        setSelectedRecord(null);
    };

    const isCreateModalOpen = modalState === 'create';
    const isEditModalOpen = modalState === 'edit';
    const isViewModalOpen = modalState === 'view';

    return (
        <PageContent className={clsx('building_room_details', props?.className)}>
            <PageSection>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col span={4}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Threshold List')}</Text>
                                </PageTitle>
                            </Col>

                        </Row>
                    </Col>
                    <Col span={24}>
                        {
                            listingData ?
                                <ComplexTable fetchData={listingData} language={i18next.resolvedLanguage}
                                    tableConfiguration={{ collumnsConfiguration: ThresholdsTableConfig }}
                                    editButton={allowedToWrite}
                                    isLoading={isFetchingThresholds}
                                    deleteButton={allowedToWrite}
                                    detailsButton={true}
                                    fixedActions={true}
                                    handleDelete={(record) => {
                                        setThresholdToDelete({ ID: record.thresholdId, Label: record.label, Type: record.type, "Open Threshold": record.openThreshold });
                                    }}
                                    pagination={{ pageSize: 5 }}
                                    handleEdit={(record) => {
                                        setSelectedRecord({ ...thresholdEmptyRecord, ...record });
                                        setSelectedItem(record);
                                        openModal('edit');
                                    }}
                                    handleDetails={(record) => {
                                        setSelectedRecord({ ...thresholdEmptyRecord, ...record });
                                        setSelectedItem(record);
                                        openModal('view');
                                    }}
                                    customizedButtons={
                                        allowedToWrite && [
                                            {
                                                title: t('create'),
                                                function: () => {
                                                    setSelectedRecord(thresholdEmptyRecord);
                                                    openModal('create');
                                                },
                                                type: "-blue"
                                            },
                                        ]}
                                />
                                :
                                <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Spinner stroke="green" />
                                </div>
                        }
                    </Col>
                    <ThresholdsModal
                        isCreateModalOpen={isCreateModalOpen}
                        isEditModalOpen={isEditModalOpen}
                        isViewModalOpen={isViewModalOpen}
                        onCreateModalClose={closeModal}
                        onEditModalClose={closeModal}
                        onViewModalClose={closeModal}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        selectedRecord={selectedRecord}
                        onCreateOrEditSuccess={() => refetchThresholdList()}
                    />
                </Row>
            </PageSection>
        </PageContent>
    )
}